import { Link } from 'react-router-dom';
import { ArrowRight, Coffee, Users, MapPin, Clock, Handshake, Store, Wrench, Utensils, Check, Home } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { SEO } from '../../components/SEO';

export default function BourgEnBressePage() {
  return (
    <main data-testid="seo-page-bourg-en-bresse" className="pt-32">
      <SEO 
        title="Création de site web à Bourg-en-Bresse (01) | Webdesigner freelance Ain"
        description="Webdesigner freelance basé à Bourg-en-Bresse. Création de sites web pour artisans, commerces et entreprises de la Bresse. Péronnas, Viriat, Attignat. Devis gratuit sous 48h."
        path="/creation-site-web-bourg-en-bresse"
      />

      {/* Hero Section */}
      <section className="container-main mb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/images/seo/ain.png" 
                alt="Ain" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-sm uppercase tracking-widest font-bold text-primary">
                Ain • Bresse
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
              Création de site web
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700 pb-2">
                à Bourg-en-Bresse
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
              Webdesigner freelance basé à <strong>Bourg-en-Bresse</strong>, je crée des sites web sur-mesure pour les artisans, commerces et entreprises de la Bresse. <strong>Rencontrons-nous pour en parler.</strong>
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/tarifs#contact">
                <Button className="btn-primary text-lg px-8 py-6">
                  Prendre rendez-vous
                  <Coffee className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/tarifs">
                <Button variant="outline" className="text-lg px-8 py-6">
                  Voir les tarifs
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Card */}
          <div className="animate-fade-up animation-delay-200">
            <Card className="overflow-hidden border-2 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6">Bourg-en-Bresse en chiffres</h2>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center p-4 bg-white/10 rounded-xl">
                    <div className="text-3xl font-black">42 000</div>
                    <div className="text-sm opacity-80">habitants</div>
                  </div>
                  <div className="text-center p-4 bg-white/10 rounded-xl">
                    <div className="text-3xl font-black">Préfecture</div>
                    <div className="text-sm opacity-80">de l'Ain</div>
                  </div>
                  <div className="text-center p-4 bg-white/10 rounded-xl col-span-2">
                    <div className="text-xl font-bold">Entre Lyon & Genève</div>
                    <div className="text-sm opacity-80 mt-1">
                      Au cœur d'un bassin économique dynamique
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section: Votre voisin webdesigner */}
      <section className="bg-muted/30 section-padding">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Photo centre-ville */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img 
                  src="/images/seo/bourg-terrasse.png"
                  alt="Création site web Bourg-en-Bresse - Terrasses du centre-ville"
                  className="w-full h-auto rounded-2xl object-cover"
                />
                <p className="text-xs text-muted-foreground mt-2 text-right">
                  © Bourg-en-Bresse, la belle rencontre
                </p>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Home className="h-4 w-4" />
                Basé à Bourg-en-Bresse
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Webdesigner freelance<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700">
                  à Bourg-en-Bresse
                </span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Je ne suis pas une agence parisienne qui ne connaît pas votre réalité. 
                Je suis <strong>bressan</strong>, je connais le marché de <strong>Bourg-en-Bresse</strong>, les commerçants de la rue Notre-Dame, 
                et les défis des entrepreneurs locaux.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Quand vous travaillez avec moi, vous pouvez me croiser au marché le samedi matin, 
                ou me retrouver autour d'un café pour discuter de votre projet.
              </p>
              <div className="flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl">
                <Coffee className="h-10 w-10 text-blue-600 shrink-0" />
                <div>
                  <div className="font-bold">Premier rendez-vous offert</div>
                  <div className="text-sm text-muted-foreground">On se retrouve où ça vous arrange à Bourg</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Pour les artisans & commerces */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest font-bold text-primary mb-4 block">
              Pour qui ?
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Création de site web pour<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700">
                artisans & commerces à Bourg-en-Bresse
              </span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Je travaille avec les professionnels de Bourg-en-Bresse et environs qui veulent une présence en ligne efficace, sans jargon technique.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card: Artisans */}
            <Card className="group hover:border-blue-500/50 transition-all hover:shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Wrench className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Artisans</h3>
                <p className="text-muted-foreground mb-4">
                  Plombiers, électriciens, menuisiers, maçons... Montrez votre savoir-faire et recevez des demandes de devis en ligne.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Galerie de réalisations
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Formulaire de devis
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Référencement local
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: Commerces */}
            <Card className="group hover:border-blue-500/50 transition-all hover:shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Store className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Commerces</h3>
                <p className="text-muted-foreground mb-4">
                  Boutiques, salons de coiffure, instituts de beauté... Attirez de nouveaux clients et fidélisez les existants.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Horaires & localisation
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Prise de rendez-vous
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Avis clients
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: Restaurants */}
            <Card className="group hover:border-blue-500/50 transition-all hover:shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Utensils className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Restaurants</h3>
                <p className="text-muted-foreground mb-4">
                  Tables bressanes, bouchons, bistrots... Mettez en valeur votre carte et remplissez vos réservations.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Menu en ligne
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Réservation en ligne
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Photos appétissantes
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section: L'avantage du local */}
      <section className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white section-padding">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm uppercase tracking-widest font-bold text-blue-200 mb-4 block">
                Pourquoi travailler avec moi ?
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                L'avantage de travailler<br />
                avec quelqu'un du coin
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                    <Handshake className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">On se rencontre en vrai</h3>
                    <p className="text-blue-100">
                      Pas de visio impersonnelle. On se retrouve à Bourg, on discute de votre projet autour d'un café.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Je connais votre marché</h3>
                    <p className="text-blue-100">
                      Je sais ce qui fonctionne ici. Je connais vos concurrents, vos clients potentiels, vos enjeux.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                    <Users className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Une relation de confiance</h3>
                    <p className="text-blue-100">
                      On se croise en ville, on se connaît. Je m'investis dans votre réussite parce que votre succès, c'est aussi ma réputation.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Photo café */}
            <div>
              <div className="relative">
                <img 
                  src="/images/seo/colleagues.jpg"
                  alt="Discussion autour d'un café"
                  className="w-full h-auto rounded-2xl object-cover"
                />
                <p className="text-xs text-blue-200 mt-2 text-right">
                  © Freepik
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Zones couvertes */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Création de site web à Bourg-en-Bresse et environs
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Basé à Bourg-en-Bresse, je me déplace à Péronnas, Viriat, Attignat et dans toute la Bresse.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Bourg-en-Bresse', desc: 'Centre-ville, Cenord, Brou' },
              { name: 'Péronnas', desc: 'Zone commerciale' },
              { name: 'Viriat', desc: 'Entreprises et commerces' },
              { name: 'Saint-Denis-lès-Bourg', desc: 'Zone d\'activité' },
              { name: 'Attignat', desc: 'Artisans et PME' },
              { name: 'Montrevel-en-Bresse', desc: 'Cœur de la Bresse' },
            ].map((zone, index) => (
              <Card key={zone.name} className="hover:border-blue-500/50 transition-colors">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-white font-bold shrink-0">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="font-bold">{zone.name}</h3>
                    <p className="text-sm text-muted-foreground">{zone.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-muted/30">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Votre site web à Bourg-en-Bresse
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Création de site internet à Bourg-en-Bresse et environs. Premier rendez-vous gratuit, devis sous 48h.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/tarifs#contact">
                <Button className="btn-primary text-lg px-8 py-6 w-full sm:w-auto">
                  Prendre rendez-vous
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/processus">
                <Button variant="outline" className="text-lg px-8 py-6 w-full sm:w-auto">
                  Découvrir mon processus
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-6 text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Bourg-en-Bresse (01)</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>Devis sous 48h</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
